import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../../base/dialog';
import { useIsWithinTailWindBreakpoint } from '../../hooks/useIsWithinTailWindBreakpoint';
import { NoddiButton } from '../Inputs/NoddiButton';
import { onOverlayOpenAutoFocus } from './onOverlayOpenAutoFocus';

interface BaseNoddiAsyncConfirmationDialogProps {
  /** loading state of action button */
  confirmActionLoading?: boolean;
  /** Action to execute when user confirms - must be awaited */
  confirmAction: () => Promise<void>;
  /** The dialog title */
  title: string;
  /** The dialog description */
  description?: string;
  /** Custom text for the cancel button */
  cancelText?: string;
  /** Custom text for the confirm/proceed button */
  confirmText?: string;
  children?: React.ReactNode;
  confirmActionDisabled?: boolean;
}

export type NoddiAsyncConfirmationDialogProps = BaseNoddiAsyncConfirmationDialogProps &
  (
    | {
        /** The trigger element that opens the dialog in uncontrolled mode */
        trigger: React.ReactNode;
        open?: never;
        onOpenChange?: (open: boolean) => void;
      }
    | {
        /** Controlled mode: whether the dialog is open */
        open: boolean;
        /** Controlled mode: callback when dialog open state changes */
        onOpenChange: (open: boolean) => void;
        trigger?: never;
      }
  );

export const NoddiAsyncConfirmationDialog = ({
  confirmActionLoading,
  title,
  description = '',
  trigger,
  open,
  onOpenChange,
  children,
  confirmAction,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  confirmActionDisabled
}: NoddiAsyncConfirmationDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open : internalOpen;
  const setDialogOpen = isControlled ? onOpenChange : setInternalOpen;

  const isMdOrSmaller = useIsWithinTailWindBreakpoint('md');

  // Held for the whole confirmAction, so a second click while it runs cannot
  // start it again (callers' own loading flags may cover only one request).
  // The ref closes the gap before the state re-renders, when two clicks land in the same tick.
  const [isConfirming, setIsConfirming] = useState(false);
  const isConfirmingRef = useRef(false);

  const handleConfirmAction = async () => {
    if (isConfirmingRef.current) {
      return;
    }
    isConfirmingRef.current = true;
    setIsConfirming(true);
    try {
      await confirmAction();
      setDialogOpen(false);
    } finally {
      isConfirmingRef.current = false;
      setIsConfirming(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent onOpenAutoFocus={onOverlayOpenAutoFocus}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {children && <div className='max-h-[60vh] overflow-auto sm:py-2'>{children}</div>}
        <DialogFooter>
          <DialogClose asChild>
            <NoddiButton size={isMdOrSmaller ? 'small' : 'default'} className='w-full sm:w-fit' variant='outline'>
              {cancelText}
            </NoddiButton>
          </DialogClose>
          <NoddiButton
            size={isMdOrSmaller ? 'small' : 'default'}
            className='w-full sm:w-fit'
            loading={confirmActionLoading || isConfirming}
            // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleConfirmAction(); React discards the returned promise, so a rejection is unhandled unless the body catches
            onClick={handleConfirmAction}
            disabled={confirmActionDisabled || isConfirming}
          >
            {confirmText}
          </NoddiButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
